import React, {Component} from 'react';
import PropTypes from "prop-types";
import Api from "../../utils/Api";
import TableContainer from "../../utils/TableContainer";
import InputField from "../../utils/InputField";
import {confirmAlert} from "react-confirm-alert";
import '../../css/style-confirmAlert.css';
import ReactHtmlParser from 'react-html-parser';

class ApiOverviewContainer extends Component {

    static propTypes = {
        baseData: PropTypes.object.isRequired,
    };

    constructor(props) {

        super(props);

        this.api = new Api();

        this.state = {
            apiKeys: [],
            newKeyLabel: '',
            isLoading: false,
            aiDisclaimerAccepted: false
        };

        this.translate = this.props.baseData.translate;
        this.requestApiKeys = this.requestApiKeys.bind(this);
        this.generateApiKey = this.generateApiKey.bind(this);
        this.deleteApiKey = this.deleteApiKey.bind(this);
        this.onLabelChange = this.onLabelChange.bind(this);

    }

    componentDidMount() {
        this.requestApiKeys();
    }

    componentDidUpdate(prevProps) {
        const prevCompanies = prevProps.baseData.companies;
        const currentCompanies = this.props.baseData.companies;
        
        const prevCompanyId = prevCompanies ? (prevCompanies.getCurrentCompany() ? prevCompanies.getCurrentCompany().id : null) : null;
        const currentCompanyId = currentCompanies ? (currentCompanies.getCurrentCompany() ? currentCompanies.getCurrentCompany().id : null) : null;

        if (prevCompanyId !== currentCompanyId) {
            this.requestApiKeys();
        }
    }

    requestApiKeys() {
        const companies = this.props.baseData.companies;
        const currentCompany = companies ? companies.getCurrentCompany() : null;
        const companyId = currentCompany ? currentCompany.id : this.props.baseData.userData.idCompany;

        this.lastRequestCompanyId = companyId;
        this.setState({apiKeys: []});

        const params = {
            action: 'getApiKeys',
            currentCompanyId: companyId
        };

        const callback = function (res) {
            if (this.lastRequestCompanyId === companyId) {
                this.setState({
                    apiKeys: (res && Array.isArray(res)) ? res : []
                });
            }
        }.bind(this);

        this.api.request('AdminFunctions', params, callback, (err) => {
            if (this.lastRequestCompanyId === companyId) {
                this.setState({apiKeys: []});
                console.error(err);
            }
        });
    }

    onLabelChange(event, name, value) {
        this.setState({[name]: value});
    }

    copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(() => {
                alert(this.translate("keyCopiedToClipboard"));
            });
        } else {
            // Fallback
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                alert(this.translate("keyCopiedToClipboard"));
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
            }
            document.body.removeChild(textArea);
        }
    }

    generateApiKey() {
        const companies = this.props.baseData.companies;
        const currentCompany = companies ? companies.getCurrentCompany() : null;
        const companyId = currentCompany ? currentCompany.id : this.props.baseData.userData.idCompany;

        const params = {
            action: 'generateApiKey',
            currentCompanyId: companyId,
            label: this.state.newKeyLabel
        };

        const callback = function (res) {
            if (res && res.success) {
                this.setState({newKeyLabel: ''});
                this.requestApiKeys();
            } else if (res && res.error) {
                alert(res.error);
            }
        }.bind(this);

        this.api.request('AdminFunctions', params, callback, null);
    }

    deleteApiKey(id) {
        confirmAlert({
            title: this.translate('deleteApiKey'),
            message: this.translate('deleteApiKeyQuestion'),
            buttons: [
                {
                    label: this.translate('yes'),
                    onClick: () => {
                        const companies = this.props.baseData.companies;
                        const currentCompany = companies ? companies.getCurrentCompany() : null;
                        const companyId = currentCompany ? currentCompany.id : this.props.baseData.userData.idCompany;

                        const params = {
                            action: 'deleteApiKey',
                            id: id,
                            currentCompanyId: companyId
                        };

                        const callback = function (res) {
                            if (res && res.success) {
                                this.requestApiKeys();
                            }
                        }.bind(this);

                        this.api.request('AdminFunctions', params, callback, null);
                    }
                },
                {
                    label: this.translate('no')
                }
            ]
        });
    }

    render() {
        const { apiKeys, newKeyLabel } = this.state;
        const canGenerate = apiKeys.length < 3;

        const columns = [
            { key: 'label', nameKey: 'label', classes: 'text-left' },
            { key: 'key_code', nameKey: 'key', classes: 'text-left' },
            { key: 'created_at', nameKey: 'createdAt', classes: 'text-left' },
            { key: 'actions', nameKey: 'actions', classes: 'text-center' }
        ];

        const rows = apiKeys.map(key => ({
            ...key,
            key_code: (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <code style={{marginRight: '10px'}}>{key.key}</code>
                    <a className="formButton" onClick={() => this.copyToClipboard(key.key)} style={{padding: '5px 15px', minWidth: '80px', margin: '0'}}>
                        {this.translate("copy")}
                    </a>
                </div>
            ),
            created_at: key.created_at,
            actions: (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <a className="formButton delete" onClick={() => this.deleteApiKey(key.id)} style={{padding: '5px 15px', minWidth: '80px', margin: '0'}}>
                        {this.translate("delete")}
                    </a>
                </div>
            )
        }));

        const companies = this.props.baseData.companies;
        const currentCompany = companies ? companies.getCurrentCompany() : null;
        const userData = this.props.baseData.userData;
        const aiConnectorEnabled = (userData && userData.ai_connector_enabled) || (currentCompany && currentCompany.ai_connector_enabled);

        return (
            <div className="formSettings">
                <nav>
                    <h1>{this.translate("apiKeyManagement")}</h1>
                </nav>

                <div className="formRow">
                    <h3>{this.translate("activeApiKeys")} ({apiKeys.length}/3)</h3>
                    <TableContainer
                        baseData={this.props.baseData}
                        columns={columns}
                        rows={rows}
                        noDataKey="noActiveApiKeysFound"
                    />
                </div>

                {canGenerate && (
                    <div className="formRow">
                        <h3>{this.translate("generateNewKey")}</h3>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="text-left labelSpan">{this.translate("label")}</td>
                                    <td className="text-left">
                                        <InputField
                                            name="newKeyLabel"
                                            value={newKeyLabel}
                                            onChange={this.onLabelChange}
                                            placeholder={this.translate("labelExample")}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="text-left">
                                        <a className="formButton" onClick={this.generateApiKey}>
                                            {this.translate("generateKey")}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                <br/><br/>
                <div className="formRow">
                    <h3>{this.translate("documentation")}</h3>
                    <a href="/customer-api/api-docs/" target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'underline'}}>
                        {this.translate("openSwaggerDocumentation")}
                    </a>
                </div>

                {aiConnectorEnabled && <><br/><br/>
                <div className="formRow" id={"aiUsage"}>
                    <h3>{this.translate("aiUsageClaudeCodeSkill", "Nutzung mit AI - Claude Code Skill")}</h3>
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6',
                        textAlign: 'left'
                    }}>
                        {this.state.aiDisclaimerAccepted
                            ? this.renderAiSkillDocumentation()
                            : this.renderAiDisclaimer()
                        }
                    </div>
                </div>
                </>}
            </div>
        );
    }

    renderAiDisclaimer() {
        return (
            <div>
                <p style={{whiteSpace: 'pre-wrap', marginBottom: '20px', fontWeight: 'bold'}}>
                    {this.translate("aiDisclaimerTitle")}
                </p>
                <p style={{whiteSpace: 'pre-wrap', marginBottom: '20px'}}>

                    {this.translate("aiDisclaimer")}
                </p>
                <a className="formButton" onClick={() => this.setState({aiDisclaimerAccepted: true})}>
                    {this.translate("agree")}
                </a>
            </div>
        );
    }

    renderAiSkillDocumentation() {
        const lang = this.props.baseData.i18n ? this.props.baseData.i18n.language : 'en';
        let content = '';

        const skillDe = `
            <p>Der <strong>MyZSK Report Studio Skill</strong> verbindet Claude Code mit der MyZSK Customer API. Stellen Sie eine analytische Frage in natürlicher Sprache, und Claude ruft die Daten ab, erstellt einen dauerhaften HTML-Bericht mit Diagrammen und Tabellen und speichert diesen lokal. Alle Berichte sind über eine Browserseite (<code>~/zsk-reports/index.html</code>) durchsuchbar, wiederverwendbar und versioniert — kein Server, keine Datenbank erforderlich.</p>
            <p>Der Skill unterstützt die Benutzeroberflächen in Deutsch, Englisch und Italienisch (umschaltbar über die Sprachschaltflächen oben rechts auf der Browserseite; die Standardeinstellung wird aus Ihrem Browser-Gebietsschema erkannt).</p>
            
            <h4 style="font-weight: bold;">Voraussetzungen</h4>
            <ul>
                <li>Python 3.11+ (<a href="https://python.org" target="_blank" rel="noopener noreferrer">python.org</a>)</li>
                <li>Claude Code installiert (<a href="https://claude.ai/claude-code" target="_blank" rel="noopener noreferrer">claude.ai/claude-code</a>)</li>
                <li>MyZSK Customer API-Schlüssel</li>
                <li>Etwa 5 MB Festplattenspeicher für Vendor-Bibliotheken</li>
            </ul>

            <h4 style="font-weight: bold;">Einrichtung (einmalig)</h4>
            <p style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 8px 12px; margin-bottom: 8px;">
                <strong>Terminal öffnen (Windows):</strong> Drücken Sie <code>Windows-Taste + X</code> und wählen Sie „Windows PowerShell" oder „Terminal" — oder suchen Sie im Startmenü nach „PowerShell".
            </p>
            <pre style="background: #e9ecef; padding: 10px; border-radius: 4px;">
# 1. Laden Sie das Skill-ZIP von dieser Seite herunter und entpacken Sie es
#    (z.B. zsk-reports.zip → zsk-reports/), wechseln Sie dann in diesen Ordner:
cd zsk-reports

# 2. Python-Umgebung + Abhängigkeiten
python3 -m venv .venv

# Umgebung aktivieren — Windows (PowerShell):
.venv\\Scripts\\activate
# Umgebung aktivieren — macOS / Linux:
# source .venv/bin/activate

pip install -r requirements.txt

# 3. Konfiguration
cp .env.example .env
# Öffnen Sie .env in einem Editor und tragen Sie Ihren ZSK_API_KEY ein

# 4. Erstellen Sie den Berichtsordner + Vendor-Bibliotheken (~1,5 MB: ECharts, Tabulator, Tailwind)
python3 scripts/setup_reports_dir.py

# 5. API-Test — überprüft, ob Ihr Schlüssel und Ihre URL funktionieren
python3 scripts/api_test.py

# 6. Registrieren Sie den Skill in Claude Code
python3 scripts/install_skill.py
# Um auf eine neuere Version zu aktualisieren: Entpacken Sie das neue Paket und führen Sie es erneut mit --force aus.
            </pre>
            <p><a href="zsk-reports.zip" download style="color: #007bff; text-decoration: underline; font-weight: bold;">Download Skill ZIP</a></p>

            <h4 style="font-weight: bold;">Nutzung</h4>
            <ol style="margin-left:30px;">
                <li><strong>Starten Sie Claude Code</strong> — in einem beliebigen Verzeichnis</li>
                <li><strong>Stellen Sie eine Frage</strong>, zum Beispiel:
                    <ul>
                        <li><em>"Welche Maschinen sind aktuell konfiguriert?"</em></li>
                        <li><em>"Zeige mir die Stickleistung der letzten Woche nach Maschine."</em></li>
                        <li><em>"Welche Designs hatten in den letzten 30 Tagen die meisten Fadenbrüche?"</em></li>
                        <li><em>"Vergleiche die Bedienerleistung diesen Monat mit dem letzten Monat."</em></li>
                    </ul>
                </li>
                <li><strong>Öffnen Sie den Bericht</strong>: Claude zeigt am Ende jedes Laufs den Dateipfad an — oder öffnen Sie <code>~/zsk-reports/index.html</code> in Ihrem Browser, um alle Berichte zu durchsuchen.</li>
            </ol>

            <h4 style="font-weight: bold;">Wo sind meine Berichte?</h4>
            <p><code>~/zsk-reports/</code> — siehe <code>docs/operations.md</code> für Details zur Ordnerstruktur, erneuten Ausführungen und Diffs.</p>

            <h4 style="font-weight: bold;">Weitere Dokumentation</h4>
            <ul>
                <li><code>docs/architecture.md</code> — wie der Skill aufgebaut ist</li>
                <li><code>docs/operations.md</code> — tägliche Nutzung (erneute Ausführung, Diff, Favoriten, Tags)</li>
                <li><code>docs/troubleshooting.md</code> — wenn etwas schief geht</li>
                <li><code>docs/scope-boundary.md</code> — was der Skill bewusst nicht tut (im Vergleich zum MY.ZSK Webtool)</li>
                <li><code>references/endpoints.md</code> — API-Endpunktkatalog mit exakten Feldnamen</li>
                <li><code>references/gotchas.md</code> — bekannte Fehler/Eigenheiten der MyZSK API</li>
                <li><code>DESIGN.md</code> (für Ingenieure) — Architektur-Spezifikation</li>
                <li><code>SKILL.md</code> (für Claude) — Skill-Anweisungen, Workflows für erneute Ausführung/Diff</li>
            </ul>
        `;

        const skillEn = `
            <p>The <strong>MyZSK Report Studio Skill</strong> connects Claude Code to the MyZSK Customer API. Ask an analytical question in natural language, and Claude fetches the data, builds a persistent HTML report with charts and tables, and saves it locally. All reports are searchable, reusable, and versioned through a browser page (<code>~/zsk-reports/index.html</code>) — no server, no database required.</p>
            <p>The skill supports German, English, and Italian UI (toggle via the language buttons in the top-right corner of the browse page; default is detected from your browser locale).</p>

            <h4 style="font-weight: bold;">Prerequisites</h4>
            <ul>
                <li>Python 3.11+ (<a href="https://python.org" target="_blank" rel="noopener noreferrer">python.org</a>)</li>
                <li>Claude Code installed (<a href="https://claude.ai/claude-code" target="_blank" rel="noopener noreferrer">claude.ai/claude-code</a>)</li>
                <li>MyZSK Customer API key</li>
                <li>About 5 MB of disk space for vendor libraries</li>
            </ul>

            <h4 style="font-weight: bold;">Setup (one-time)</h4>
            <p style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 8px 12px; margin-bottom: 8px;">
                <strong>Opening a terminal (Windows):</strong> Press <code>Windows key + X</code> and choose "Windows PowerShell" or "Terminal" — or search for "PowerShell" in the Start menu.
            </p>
            <pre style="background: #e9ecef; padding: 10px; border-radius: 4px;">
# 1. Download the skill ZIP from this page and unzip it
#    (e.g. zsk-reports.zip → zsk-reports/), then change into that folder:
cd zsk-reports

# 2. Python environment + dependencies
python3 -m venv .venv

# Activate environment — Windows (PowerShell):
.venv\\Scripts\\activate
# Activate environment — macOS / Linux:
# source .venv/bin/activate

pip install -r requirements.txt

# 3. Configuration
cp .env.example .env
# Open .env in an editor and fill in your ZSK_API_KEY

# 4. Create the reports folder + vendor libraries (~1.5 MB: ECharts, Tabulator, Tailwind)
python3 scripts/setup_reports_dir.py

# 5. API test — verifies that your key and URL work
python3 scripts/api_test.py

# 6. Register the skill in Claude Code
python3 scripts/install_skill.py
# To update to a newer version: unzip the new package and re-run with --force.
            </pre>
            <p><a href="zsk-reports.zip" download style="color: #007bff; text-decoration: underline; font-weight: bold;">Download Skill ZIP</a></p>

            <h4 style="font-weight: bold;">Usage</h4>
            <ol style="margin-left:30px;">
                <li><strong>Start Claude Code</strong> — in any directory</li>
                <li><strong>Ask a question</strong>, for example:
                    <ul>
                        <li><em>"Which machines are currently configured?"</em></li>
                        <li><em>"Show me embroidery performance for last week, by machine."</em></li>
                        <li><em>"Which designs had the most thread breaks in the last 30 days?"</em></li>
                        <li><em>"Compare operator performance this month vs. last month."</em></li>
                    </ul>
                </li>
                <li><strong>Open the report</strong>: Claude shows the file path at the end of each run — or open <code>~/zsk-reports/index.html</code> in your browser to browse all reports.</li>
            </ol>

            <h4 style="font-weight: bold;">Where are my reports?</h4>
            <p><code>~/zsk-reports/</code> — see <code>docs/operations.md</code> for details on folder structure, re-runs, and diffs.</p>

            <h4 style="font-weight: bold;">Further documentation</h4>
            <ul>
                <li><code>docs/architecture.md</code> — how the skill is built</li>
                <li><code>docs/operations.md</code> — day-to-day usage (re-run, diff, favorites, tags)</li>
                <li><code>docs/troubleshooting.md</code> — when something goes wrong</li>
                <li><code>docs/scope-boundary.md</code> — what the skill deliberately does not do (vs. MY.ZSK Webtool)</li>
                <li><code>references/endpoints.md</code> — API endpoint catalog with exact field names</li>
                <li><code>references/gotchas.md</code> — known bugs/quirks of the MyZSK API</li>
                <li><code>DESIGN.md</code> (for engineers) — architecture spec</li>
                <li><code>SKILL.md</code> (for Claude) — skill instructions, re-run/diff workflows</li>
            </ul>
        `;

        const skillIt = `
            <p>Lo <strong>MyZSK Report Studio Skill</strong> collega Claude Code all'API MyZSK Customer. Poni una domanda analitica in linguaggio naturale e Claude recupera i dati, crea un report HTML persistente con grafici e tabelle e lo salva localmente. Tutti i report sono ricercabili, riutilizzabili e versionati tramite una pagina del browser (<code>~/zsk-reports/index.html</code>) — nessun server, nessun database richiesto.</p>
            <p>Lo skill supporta l'interfaccia utente in tedesco, inglese e italiano (commutabile tramite i pulsanti della lingua nell'angolo in alto a destra della pagina del browser; l'impostazione predefinita viene rilevata dalle impostazioni locali del browser).</p>

            <h4 style="font-weight: bold;">Prerequisiti</h4>
            <ul>
                <li>Python 3.11+ (<a href="https://python.org" target="_blank" rel="noopener noreferrer">python.org</a>)</li>
                <li>Claude Code installato (<a href="https://claude.ai/claude-code" target="_blank" rel="noopener noreferrer">claude.ai/claude-code</a>)</li>
                <li>Chiave API MyZSK Customer</li>
                <li>Circa 5 MB di spazio su disco per le librerie vendor</li>
            </ul>

            <h4 style="font-weight: bold;">Configurazione (una tantum)</h4>
            <p style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 8px 12px; margin-bottom: 8px;">
                <strong>Aprire un terminale (Windows):</strong> Premere <code>Tasto Windows + X</code> e scegliere "Windows PowerShell" o "Terminale" — oppure cercare "PowerShell" nel menu Start.
            </p>
            <pre style="background: #e9ecef; padding: 10px; border-radius: 4px;">
# 1. Scarica lo ZIP dello skill da questa pagina e decomprimilo
#    (es. zsk-reports.zip → zsk-reports/), quindi entra in quella cartella:
cd zsk-reports

# 2. Ambiente Python + dipendenze
python3 -m venv .venv

# Attivare l'ambiente — Windows (PowerShell):
.venv\\Scripts\\activate
# Attivare l'ambiente — macOS / Linux:
# source .venv/bin/activate

pip install -r requirements.txt

# 3. Configurazione
cp .env.example .env
# Apri .env in un editor e inserisci la tua ZSK_API_KEY

# 4. Crea la cartella dei report + librerie vendor (~1.5 MB: ECharts, Tabulator, Tailwind)
python3 scripts/setup_reports_dir.py

# 5. Test API — verifica che la tua chiave e l'URL funzionino
python3 scripts/api_test.py

# 6. Registra lo skill in Claude Code
python3 scripts/install_skill.py
# Per aggiornare a una versione più recente: decomprimi il nuovo pacchetto e riesegui con --force.
            </pre>
            <p><a href="zsk-reports.zip" download style="color: #007bff; text-decoration: underline; font-weight: bold;">Download Skill ZIP</a></p>

            <h4 style="font-weight: bold;">Utilizzo</h4>
            <ol style="margin-left:30px;">
                <li><strong>Avvia Claude Code</strong> — in qualsiasi directory</li>
                <li><strong>Poni una domanda</strong>, per esempio:
                    <ul>
                        <li><em>"Quali macchine sono attualmente configurate?"</em></li>
                        <li><em>"Mostrami le prestazioni di ricamo dell'ultima settimana, per macchina."</em></li>
                        <li><em>"Quali design hanno avuto più rotture di filo negli ultimi 30 giorni?"</em></li>
                        <li><em>"Confronta le prestazioni dell'operatore questo mese rispetto al mese scorso."</em></li>
                    </ul>
                </li>
                <li><strong>Apri il report</strong>: Claude mostra il percorso del file alla fine di ogni esecuzione — oppure apri <code>~/zsk-reports/index.html</code> nel tuo browser per sfogliare tutti i report.</li>
            </ol>

            <h4 style="font-weight: bold;">Dove sono i miei report?</h4>
            <p><code>~/zsk-reports/</code> — vedere <code>docs/operations.md</code> per i dettagli sulla struttura delle cartelle, le ri-esecuzioni e i diff.</p>

            <h4 style="font-weight: bold;">Ulteriore documentazione</h4>
            <ul>
                <li><code>docs/architecture.md</code> — come è costruito lo skill</li>
                <li><code>docs/operations.md</code> — utilizzo quotidiano (ri-esecuzione, diff, preferiti, tag)</li>
                <li><code>docs/troubleshooting.md</code> — quando qualcosa va storto</li>
                <li><code>docs/scope-boundary.md</code> — cosa lo skill deliberatamente non fa (rispetto al MY.ZSK Webtool)</li>
                <li><code>references/endpoints.md</code> — catalogo degli endpoint API con nomi di campo esatti</li>
                <li><code>references/gotchas.md</code> — bug/particolarità noti dell'API MyZSK</li>
                <li><code>DESIGN.md</code> (per engineers) — specifica dell'architettura</li>
                <li><code>SKILL.md</code> (for Claude) — istruzioni dello skill, workflow di ri-esecuzione/diff</li>
            </ul>
        `;

        if (lang.startsWith('de')) {
            content = skillDe;
        } else if (lang.startsWith('it')) {
            content = skillIt;
        } else {
            content = skillEn;
        }

        return ReactHtmlParser(content);
    }
}

export default ApiOverviewContainer;
